import { CanvasElementType } from '@blocksuite/affine-block-surface';
import { TelemetryProvider } from '@blocksuite/affine-shared/services';
import { Bound } from '@blocksuite/global/gfx';
import type { GfxController } from '@blocksuite/std/gfx';

import {
  DEFAULT_MERMAID_TEMPLATE,
  getMermaidTemplate,
  type MermaidTemplateId,
} from './templates.js';

export const MERMAID_DEFAULT_WIDTH = 480;
export const MERMAID_DEFAULT_HEIGHT = 320;

const COLLISION_STEP = 36;
const COLLISION_ATTEMPTS = 24;

export type MermaidCreationControl =
  | 'canvas:draw'
  | 'toolbar:dnd'
  | 'toolbar:general';

export function getCollisionAwareMermaidBound(
  gfx: GfxController,
  center: readonly [number, number],
  width = MERMAID_DEFAULT_WIDTH,
  height = MERMAID_DEFAULT_HEIGHT
) {
  const [cx, cy] = center;

  for (let index = 0; index < COLLISION_ATTEMPTS; index++) {
    const ring = Math.floor((index + 3) / 4);
    const direction = index % 4;
    const dx =
      index === 0
        ? 0
        : direction === 0
          ? ring * COLLISION_STEP
          : direction === 2
            ? -ring * COLLISION_STEP
            : 0;
    const dy =
      index === 0
        ? 0
        : direction === 1
          ? ring * COLLISION_STEP
          : direction === 3
            ? -ring * COLLISION_STEP
            : ring * COLLISION_STEP;

    const candidate = new Bound(
      cx - width / 2 + dx,
      cy - height / 2 + dy,
      width,
      height
    );

    if (gfx.getElementsByBound(candidate, { type: 'all' }).length === 0) {
      return candidate;
    }
  }

  return new Bound(
    cx - width / 2 + COLLISION_STEP,
    cy - height / 2 + COLLISION_STEP,
    width,
    height
  );
}

export function addMermaidElement(
  gfx: GfxController,
  bound: Bound,
  templateId: MermaidTemplateId = DEFAULT_MERMAID_TEMPLATE
) {
  const template = getMermaidTemplate(templateId);
  return gfx.surface!.addElement({
    type: CanvasElementType.MERMAID,
    source: template.source,
    xywh: bound.serialize(),
    schemaVersion: 1,
    themeMode: 'auto',
    fitMode: 'content',
  });
}

export function trackMermaidCreated(
  gfx: GfxController,
  templateId: MermaidTemplateId,
  control: MermaidCreationControl,
  creationMode: 'tool' | 'drag' | 'template'
) {
  gfx.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control,
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: CanvasElementType.MERMAID,
    other: {
      template: templateId,
      creationMode,
    },
  });
}
